import { FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { KPICardData } from "../../types/kpi";
import { decodeApiDuration, formatDashboardValueString } from "src/utils/formatDashboardValue";
import { renderUniqueDashboardKpiLucideIcons } from "src/utils/dashboardKpiLucideIcon";
import { DashboardMetricTile } from "src/components/common";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { toWesternDigitsInString } from "src/utils/numberFormat";
import { formatMetricDuration } from "../../utils/webAnalyticsFormat";
import "./KPIDashboard.scss";

type KpiFormulaKey =
  | "totalUsers"
  | "totalSessions"
  | "avgBounceRate"
  | "avgSessionsPerUser"
  | "avgSessionDuration"
  | "totalPageviews"
  | "totalRevenue"
  | "keyEventRate";

function normalizeKpiIdentifier(value: string): string {
  return value
    .toLowerCase()
    .replaceAll(/[^a-z0-9/ ]/g, " ")
    .replaceAll(/\s+/g, " ")
    .trim();
}

/**
 * Resolves the metric a KPI card describes. Matched against the card `id` first and only
 * then its `title`: the API localizes `title`, so title-only matching silently loses every
 * tooltip, formula and value formatting under `lang=ar`.
 */
function getKpiFormulaKey(data: KPICardData): KpiFormulaKey | null {
  return matchKpiFormulaKey(data.id) ?? matchKpiFormulaKey(data.title);
}

function matchKpiFormulaKey(identifier: string): KpiFormulaKey | null {
  const normalized = normalizeKpiIdentifier(identifier);

  if (normalized.includes("avgbouncerate") || normalized.includes("bounce")) {
    return "avgBounceRate";
  }

  if (normalized.includes("total users") || normalized === "users") {
    return "totalUsers";
  }
  if (normalized.includes("total sessions") || normalized === "sessions") {
    return "totalSessions";
  }
  if (normalized.includes("bounce rate")) {
    return "avgBounceRate";
  }
  if (
    normalized.includes("sessions/user") ||
    normalized.includes("sessions per user") ||
    normalized.includes("avg sessions user")
  ) {
    return "avgSessionsPerUser";
  }
  if (normalized.includes("session duration")) {
    return "avgSessionDuration";
  }
  if (normalized.includes("pageviews")) {
    return "totalPageviews";
  }
  if (normalized.includes("revenue")) {
    return "totalRevenue";
  }
  if (
    normalized.includes("key event rate") ||
    normalized.includes("key events rate") ||
    normalized.includes("key event")
  ) {
    return "keyEventRate";
  }

  return null;
}

/**
 * Keyed off `formulaKey` (derived from the card `id`) rather than the raw title, so the
 * duration/currency normalization below still applies when the API returns a localized title.
 */
function normalizeKpiDisplayValue(
  formulaKey: KpiFormulaKey | null,
  value: string,
  t: TFunction,
  language: string
): string {
  if (formulaKey === "avgSessionDuration") {
    const alreadyFormatted = new RegExp(
      `\\b(${[
        "minutes?",
        "seconds?",
        t("webAnalytics.duration.minute"),
        t("webAnalytics.duration.minutes"),
        t("webAnalytics.duration.second"),
        t("webAnalytics.duration.seconds"),
      ].join("|")})\\b`,
      "i"
    );
    if (alreadyFormatted.test(value)) {
      return value;
    }
    // The API sends either a "5m 30s" short form or a raw fractional-minutes number, in
    // Western or Arabic-Indic digits depending on `lang`.
    const westernDigits = toWesternDigitsInString(value);
    const shortMatch = westernDigits.trim().match(/^(?:(\d+)m\s*)?(?:(\d+)s)?$/);
    if (shortMatch && (shortMatch[1] || shortMatch[2])) {
      const mins = Number(shortMatch[1] ?? 0);
      const secs = Number(shortMatch[2] ?? 0);
      return formatMetricDuration(mins * 60 + secs, t, language);
    }
    const rawDuration = Number(
      westernDigits.replace(/[,٬]/g, "").match(/-?\d+(?:\.\d+)?/)?.[0]
    );
    if (Number.isFinite(rawDuration)) {
      return formatMetricDuration(decodeApiDuration(rawDuration), t, language);
    }
  }

  const normalizedValue =
    formulaKey === "totalRevenue" ? value.replace(/\bRs\.?\s*/gi, "PKR ") : value;

  return formatDashboardValueString(normalizedValue);
}

interface KPICardProps {
  data: KPICardData;
  icon: ReactNode;
}

const KPICard: FC<KPICardProps> = ({ data, icon }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const formulaKey = useMemo(() => getKpiFormulaKey(data), [data]);
  const isBounceRate = formulaKey === "avgBounceRate";
  const formulaDescription = formulaKey
    ? t(`webAnalytics.kpiFormulas.${formulaKey}.description`)
    : undefined;
  const formulaText = formulaKey ? t(`webAnalytics.kpiFormulas.${formulaKey}.formula`) : undefined;
  const displayValue = useMemo(
    () => normalizeKpiDisplayValue(formulaKey, data.value, t, language),
    [formulaKey, data.value, t, language]
  );

  const changeFromApi = useMemo(() => {
    const raw = data.percentageChange;
    if (raw === undefined || raw === null) {
      return undefined;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }, [data.percentageChange]);

  return (
    <DashboardMetricTile
      label={data.title}
      value={displayValue}
      icon={icon}
      tooltip={
        formulaKey && formulaDescription
          ? { description: formulaDescription, formula: formulaText }
          : undefined
      }
      formulaTitle={t("webAnalytics.kpi.formula")}
      changePercent={changeFromApi}
      changePercentFormattedValue={data.percentageChangeFormattedValue?.trim() || undefined}
      trendPolarity={isBounceRate ? "lower-is-better" : "higher-is-better"}
      trendOutcomeLabel={
        isBounceRate
          ? changeFromApi !== undefined && changeFromApi <= 0
            ? t("webAnalytics.tooltips.bounceRateDecreasePositive")
            : t("webAnalytics.tooltips.bounceRateIncreaseNegative")
          : undefined
      }
      disableChangeFallback
      changePercentTreatZeroAsMissing={false}
      hideNoTrendFallback
      comparisonLabel={t("webAnalytics.kpi.vsLastPeriod")}
      //sublabel={t("webAnalytics.kpi.differencePeriod")}
      noTrendLabel={t("webAnalytics.kpi.trendUnavailable")}
      noTrendTooltip={t("webAnalytics.kpi.trendUnavailableTooltip")}
    />
  );
};

interface KPIDashboardProps {
  kpiData?: KPICardData[];
  loading?: boolean;
  suppressSpinner?: boolean;
}

const KPIDashboard: FC<KPIDashboardProps> = ({
  kpiData = [],
  loading = false,
  suppressSpinner = false,
}) => {
  const { t } = useTranslation();
  if (loading && !suppressSpinner) {
    return (
      <div className="web-analytics-kpi-dashboard web-analytics-kpi-dashboard--empty">
        <LoadingSpinner compact />
      </div>
    );
  }

  if (loading && suppressSpinner) {
    return <div className="web-analytics-kpi-dashboard web-analytics-kpi-dashboard--empty" />;
  }

  if (!kpiData?.length) {
    return (
      <div className="web-analytics-kpi-dashboard web-analytics-kpi-dashboard--empty">
        <div className="web-analytics-kpi-dashboard__no-data">{t("webAnalytics.noData")}</div>
      </div>
    );
  }
  // Includes the card `id` alongside the title (same as the CLTV dashboard): the API localizes
  // `title`, and an Arabic title matches none of the icon keywords, so a title-only match would
  // swap every icon when the language changes.
  const kpiIcons = renderUniqueDashboardKpiLucideIcons(
    kpiData.map(card => `${card.id.replace(/[-_]+/g, " ")} ${card.title}`),
    20,
    "webAnalytics"
  );
  return (
    <div className="web-analytics-kpi-dashboard">
      <div className="kpi-grid">
        {kpiData.map((card, index) => (
          <KPICard key={card.id} data={card} icon={kpiIcons[index]} />
        ))}
      </div>
    </div>
  );
};

export default KPIDashboard;
